import React, { useEffect, useRef } from 'react';
import classes from './Barcode.module.scss';

// bwip-js bcid + scale + height + shape per API barcode format. Anything not
// listed here renders no barcode (the code text still shows) — silently, never
// an alert. `shape` drives the on-screen size cap (see Barcode.module.scss):
// 'square' 2D codes are capped to a small square; 'linear' 1D codes are capped
// by width. Only one axis is constrained so the symbol is never distorted.
const FORMATS = {
    QRCode: ['qrcode', 4, 18, 'square'],
    Code128: ['code128', 2, 10, 'linear'],
    Code39: ['code39', 2, 10, 'linear'],
    'EAN-8': ['ean8', 2, 10, 'linear'],
    'EAN-13': ['ean13', 2, 10, 'linear'],
    'UPC-E': ['upce', 4, 10, 'linear'],
    'UPC-A': ['upca', 2, 15, 'linear'],
    ITF14: ['itf14', 2, 10, 'linear'],
    PDF417: ['pdf417', 2, 10, 'linear'],
    Aztec: ['azteccode', 6, 12, 'square'],
    DataMatrix: ['datamatrix', 6, 12, 'square'],
};

const Barcode = (props) => {
    const { content } = props;
    // Render onto a ref'd canvas rather than a global id — two Barcodes mounting
    // at once (or a stale node) can't make bwip-js draw onto the wrong element.
    const canvasRef = useRef(null);
    const codeValue = content?.redemption?.code?.value;
    const codeFormat = content?.redemption?.code?.format;
    // 'square' (QR/Aztec/DataMatrix) vs 'linear' (1D) drives the size cap so the
    // symbol fills a sensible box instead of stretching to the modal width.
    const shape = FORMATS[codeFormat]?.[3] || 'linear';

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !codeValue) return;
        const spec = FORMATS[codeFormat];
        // Unknown/blank format → leave the canvas empty (the code text below still
        // shows). No alert — a bad format must never block the rest of the modal.
        if (!spec) return;
        const [bcid, scale, height] = spec;
        // Lazy-load bwip-js only when a barcode is actually revealed — keeps the
        // library out of the main bundle. ponytail: dynamic import, code-split.
        let cancelled = false;
        import('bwip-js').then((m) => {
            if (cancelled) return;
            const bwipjs = m.default || m;
            try {
                bwipjs.toCanvas(canvas, {
                    bcid,
                    text: codeValue,
                    scale,
                    height,
                    includetext: false,
                    textxalign: 'center',
                });
            } catch (e) {
                console.error('Barcode render error:', e);
            }
        });
        return () => { cancelled = true; };
    }, [codeValue, codeFormat]);

    return (
        <div className={classes.Barcode}>
            {/* Always rendered once the code is revealed — no longer hidden by a
                timer flag, which was leaving timed offers with no scannable code.
                Size is capped via the shape class (no more full-width stretch). */}
            <canvas
                ref={canvasRef}
                className={shape === 'square' ? classes.Code2D : classes.Code1D}
            ></canvas>
            <p style={{ fontSize: codeValue && codeValue.length > 24 ? '12px' : '24px' }}>
                {codeValue}
            </p>
        </div>
    );
};

export default Barcode;
